import { extractFilenameFromDisposition, triggerBrowserDownload } from '../common/utils/download';
import api from './api';

type DownloadOptions = {
  /** Se true, ignora o nome do Content-Disposition e usa o filename abaixo */
  overrideServerName?: boolean;
  /** Se true, acrescenta _YYYY-MM-DD_HH-mm-ss antes da extensão */
  appendTimestamp?: boolean;
  /** Nome base opcional (usado se overrideServerName=true ou se o servidor não enviar nome) */
  filename?: string;
};

function buildTimestamp(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}_${pad(d.getHours())}-${pad(d.getMinutes())}-${pad(d.getSeconds())}`;
}

function withTimestamp(name: string): string {
  // separa nome e extensão
  const lastDot = name.lastIndexOf('.');
  const base = lastDot > -1 ? name.slice(0, lastDot) : name;
  const ext  = lastDot > -1 ? name.slice(lastDot) : '';
  return `${base}_${buildTimestamp()}${ext || '.pdf'}`;
}

export async function downloadPdfFrom(url: string, options: DownloadOptions = {}) {
  const resp = await api.get(url, { responseType: 'blob' });

  const contentTypeHeader = resp.headers['content-type'];
  const contentType =
    typeof contentTypeHeader === 'string' ? contentTypeHeader : 'application/pdf';
  const disposition = resp.headers['content-disposition'] as string | undefined;

  // nome vindo do servidor (se existir)
  const serverName = extractFilenameFromDisposition(disposition);

  // decide o nome base
  let finalName =
    options.overrideServerName
      ? (options.filename ?? serverName ?? 'arquivo.pdf')
      : (serverName ?? options.filename ?? 'arquivo.pdf');

  // anexa timestamp se pedido
  if (options.appendTimestamp) {
    finalName = withTimestamp(finalName);
  }

  const blob = new Blob([resp.data], { type: contentType });
  triggerBrowserDownload(blob, finalName);
}

/**
 * Abre o PDF numa nova aba para visualização, sem forçar download. Usa o
 * mesmo endpoint dos demais PDFs — o servidor sempre responde
 * `Content-Disposition: attachment`, então o navegador só evita o download
 * automático porque abrimos o blob diretamente via `URL.createObjectURL`
 * em vez de deixar o `<a download>` do `triggerBrowserDownload` disparar.
 */
export async function previewPdfFrom(url: string) {
  const resp = await api.get(url, { responseType: 'blob' });
  const contentTypeHeader = resp.headers['content-type'];
  const contentType =
    typeof contentTypeHeader === 'string' ? contentTypeHeader : 'application/pdf';

  const blob = new Blob([resp.data], { type: contentType });
  const blobUrl = URL.createObjectURL(blob);
  window.open(blobUrl, '_blank', 'noopener,noreferrer');
  // libera a memória depois que a aba teve tempo de carregar o blob
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}
